import AnalyticsPeriodFilter from "../AnalyticsPeriodFilter";
import { analyticsPeriods, getChangeDisplay } from "../analyticsPeriod";
/* eslint-disable no-unused-vars */
import React from "react";
import PropTypes from "prop-types";
import { Card, Box, Typography, IconButton, Tooltip } from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import { Link } from "react-router-dom";

function TutorAnalyticsCard({ tutorsCreated, interviewInvites, recentTutors, percentageChange, period, onPeriodChange }) {
  const tutorsChange = getChangeDisplay(percentageChange.tutors);
  const interviewsChange = getChangeDisplay(percentageChange.interviews);
  const periodLabel = analyticsPeriods.find((option) => option.value === period)?.label || "7 days";
  const getTimeAgo = (date) => {
    const now = new Date();
    const createdDate = new Date(date);
    const diffTime = Math.abs(now - createdDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffMonths >= 1 && diffDays % 30 === 0)
      return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
    if (diffMonths >= 1)
      return `${diffMonths} month${diffMonths > 1 ? "s" : ""}, ${diffDays % 30} days ago`;
    if (diffWeeks === 1 && diffDays % 7 === 0) return "1 week ago";
    if (diffWeeks === 1) return `1 week, ${diffDays % 7} days ago`;
    if (diffDays % 7 === 0) return `${diffWeeks} weeks ago`;
    return `${diffWeeks} weeks, ${diffDays % 7} days ago`;
  };

  const getAvatarColor = (index) => {
    const colors = ["#4CAF50", "#2196F3", "#5C6BC0", "#8D6E63", "#FFA726", "#66BB6A"];
    return colors[index % colors.length];
  };

  return (
    <Card
      sx={{
        height: "100%",
        p: 2,
        boxShadow: "0px 3px 10px rgba(0,0,0,0.08)",
        borderRadius: 2,
      }}
    >
      {/* Header */}
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <ArgonBox display="flex" alignItems="center" gap={1}>
          <ArgonTypography variant="h6" fontWeight="bold" color="dark">
            Tutor Analytics
          </ArgonTypography>
          <Tooltip title={`Statistics for the past ${period} days. Percentage compares the previous ${period} days; N/A means no previous records.`} arrow>
            <IconButton size="small" sx={{ p: 0 }}>
              <InfoIcon sx={{ fontSize: 18, color: "#666" }} />
            </IconButton>
          </Tooltip>
        </ArgonBox>
        <AnalyticsPeriodFilter value={period} onChange={onPeriodChange} label="Tutor Analytics" />
      </ArgonBox>

      {/* Stats Section */}
      <ArgonBox display="flex" gap={3} mb={2}>
        {/* Tutors Created */}
        <ArgonBox flex={1}>
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            Tutors Created
          </ArgonTypography>
          <ArgonTypography variant="caption" color="secondary" display="block" mb={1}>
            Past {periodLabel}
          </ArgonTypography>
          <ArgonTypography variant="h3" fontWeight="bold" color="dark" mb={0.5}>
            {tutorsCreated}
          </ArgonTypography>
          <ArgonBox display="flex" alignItems="center" gap={0.5}>
            <ArgonTypography
              variant="button"
              fontWeight="bold"
              color={tutorsChange.color}
              sx={{ display: "flex", alignItems: "center" }}
            >
              {tutorsChange.label}
            </ArgonTypography>
            <ArgonTypography variant="caption" color="text">
              vs previous {periodLabel}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>

        {/* Interview Invites */}
        <ArgonBox flex={1}>
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            Interview Invites Sent
          </ArgonTypography>
          <ArgonTypography variant="caption" color="secondary" display="block" mb={1}>
            Past {periodLabel}
          </ArgonTypography>
          <ArgonTypography variant="h3" fontWeight="bold" color="dark" mb={0.5}>
            {interviewInvites}
          </ArgonTypography>
          <ArgonBox display="flex" alignItems="center" gap={0.5}>
            <ArgonTypography
              variant="button"
              fontWeight="bold"
              color={interviewsChange.color}
              sx={{ display: "flex", alignItems: "center" }}
            >
              {interviewsChange.label}
            </ArgonTypography>
            <ArgonTypography variant="caption" color="text">
              vs previous {periodLabel}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>

      {/* Recently Created Section */}
      <ArgonBox>
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <ArgonTypography variant="button" fontWeight="medium" color="dark">
            Recently Created
          </ArgonTypography>
          <Link to="/tutors" style={{ textDecoration: "none" }}>
            <ArgonTypography
              variant="caption"
              color="info"
              fontWeight="medium"
              sx={{
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View more tutors
            </ArgonTypography>
          </Link>
        </ArgonBox>

        {/* Tutor List */}
        <ArgonBox>
          {recentTutors && recentTutors.length > 0 ? (
            recentTutors.slice(0, 4).map((tutor, index) => (
              <ArgonBox
                key={tutor.id || tutor._id || index}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={1.5}
                sx={{
                  borderBottom: index < 3 ? "1px solid #f0f0f0" : "none",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={1.5}>
                  <ArgonAvatar
                    src={tutor.profileImage}
                    alt={tutor.fullName}
                    size="sm"
                    variant="rounded"
                    sx={{
                      "& img": {
                        objectFit: "cover",
                        width: "100%",
                        height: "100%",
                      },
                    }}
                  />
                  <Link
                    to={`/tutors/profile/${tutor.id || tutor._id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <ArgonTypography
                      variant="button"
                      color="dark"
                      fontWeight="medium"
                      sx={{ textDecoration: "underline", textTransform: "none" }}
                    >
                      {tutor.fullName || "Unknown Tutor"}
                    </ArgonTypography>
                  </Link>
                </ArgonBox>
                <ArgonTypography variant="caption" color="text">
                  {getTimeAgo(tutor.createdAt)}
                </ArgonTypography>
              </ArgonBox>
            ))
          ) : (
            <ArgonTypography variant="caption" color="text" textAlign="center" display="block">
              No recent tutors
            </ArgonTypography>
          )}
        </ArgonBox>
      </ArgonBox>
    </Card>
  );
}

TutorAnalyticsCard.defaultProps = {
  tutorsCreated: 0,
  interviewInvites: 0,
  recentTutors: [],
  percentageChange: {
    tutors: 0,
    interviews: 0,
  },
};

TutorAnalyticsCard.propTypes = {
  period: PropTypes.number.isRequired,
  onPeriodChange: PropTypes.func.isRequired,
  tutorsCreated: PropTypes.number,
  interviewInvites: PropTypes.number,
  recentTutors: PropTypes.array,
  percentageChange: PropTypes.shape({
    tutors: PropTypes.number,
    interviews: PropTypes.number,
  }),
};

export default TutorAnalyticsCard;
